import { lazy, Suspense, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyPopup } from '@/hooks/usePromoPopup';
import { useAppSelector } from '@/hooks/useAppSelector';
import { selectUser } from '@/slice/user';
import { openExternal } from '@/helpers/openExternal';
import { PopupCard, cardWidthVars } from './PopupCard';
import { hasSeenPromoPopup, markPromoPopupSeen, promoPopupSeenKey } from './promoPopupSession';
import { resolveCta, resolveCtaDestination, resolveSecondaryCta } from './promoPopupConditions';
import type { ResolvedCtaDestination } from './promoPopupConditions';
import './PromoPopup.scss';

// Mounted only when a support CTA is actually clicked, so the pop-up carries no
// dependency on this modal's query client on the paths that never open it.
const RequestHelp = lazy(() =>
  import('@/components/modals/RequestHelp').then((m) => ({ default: m.RequestHelp })),
);

// TCX-917 increment (auto-dismiss timer) — how often the countdown advances.
// Fine enough for the progress line to read as motion, coarse enough that a
// full 120s timer is ~1200 ticks, not tens of thousands.
const AUTO_DISMISS_TICK_MS = 100;

/**
 * TCX-917 — the ONE promotional pop-up. Replaces NewFeatureBannerConsultant and
 * NewFeatureBannerFranchisee, which were near-identical components whose
 * stylesheets declared the SAME global class names with conflicting values, so
 * bundle import order silently decided the styling of both.
 *
 * Content, colours and typography all come from the record; only geometry and
 * behaviour live in code. Placement is deliberately unchanged from the banners
 * this replaces: Layout still gates on `lg`.
 *
 * DISPLAY FREQUENCY is changed on purpose, carrying over TCX-836's rule (see
 * promoPopupSession.ts): once per browser session rather than once per page
 * load. TCX-836 implemented this against the two banner components this
 * replaces, which no longer exist, so the rule lands here instead of being
 * rewritten on top afterwards. Called out in the PR: the TCX-917 card asks for
 * display frequency to be left alone.
 *
 * TCX-917 increment (auto-dismiss timer) — dismiss behaviour is the ONE
 * exception, changed on purpose (agreed with the ticket owner, called out in
 * the PR): when the record carries an `autoDismissSeconds`, the pop-up now
 * also closes itself once that many seconds elapse, in addition to the X. No
 * `autoDismissSeconds` — every record saved before this increment, and any
 * record where the admin leaves it blank — means no timer at all: the X
 * remains the only way to close it, unchanged from before.
 */
const PromoPopup: React.FC = () => {
  const { data } = useMyPopup();
  const user = useAppSelector(selectUser);
  const navigate = useNavigate();
  const [visible, setVisible] = useState(true);
  // Both resolved ONCE per mount, and the order matters. `seenKey` is read
  // before anything writes it, so `alreadySeen` describes the state on
  // ARRIVAL — the effect below then marks this session as served without
  // hiding the pop-up it is currently showing. Re-reading storage on each
  // render would make the write dismiss the pop-up instantly.
  const [seenKey] = useState(promoPopupSeenKey);
  const [alreadySeen] = useState(() => hasSeenPromoPopup(seenKey));
  const [supportOpen, setSupportOpen] = useState(false);

  // TCX-917 increment (auto-dismiss timer) — `null` means "no timer", which
  // is also what a record with no `autoDismissSeconds` at all produces via
  // `data?.autoDismissSeconds` being `undefined`. That is the pop-up's
  // original, still-supported shape: it stays on screen until the X is
  // clicked.
  const durationMs =
    typeof data?.autoDismissSeconds === 'number' ? data.autoDismissSeconds * 1000 : null;
  const [remainingMs, setRemainingMs] = useState<number | null>(durationMs);
  // TCX-917 increment (auto-dismiss timer) — the pointer sitting over the
  // card pauses the countdown; leaving it resumes. Without this, the offer
  // disappears mid-read from exactly the person who stopped to read it.
  const [isHovered, setIsHovered] = useState(false);

  // Re-seeds the countdown when the CONFIGURED duration itself changes (a
  // different record, or a freshly published duration) — not on every poll
  // that still reports the same value, which would restart the clock on a
  // viewer who has already been looking at the pop-up for a while.
  useEffect(() => {
    setRemainingMs(durationMs);
  }, [durationMs]);

  const expired = remainingMs !== null && remainingMs <= 0;

  // The ONE interval, created only while a timer is actually running (a
  // duration is configured, the pointer is not over the card, and time is
  // still left) and torn down whenever any of that stops being true — hover
  // included, and unmount included via the effect's own cleanup, so a stray
  // tick can never fire after this component is gone.
  useEffect(() => {
    if (durationMs === null || isHovered || expired) return undefined;
    const id = setInterval(() => {
      setRemainingMs((current) =>
        current === null ? null : Math.max(current - AUTO_DISMISS_TICK_MS, 0),
      );
    }, AUTO_DISMISS_TICK_MS);
    return () => clearInterval(id);
  }, [durationMs, isHovered, expired]);

  // Dismisses through the SAME state the X handler uses, the moment the
  // countdown reaches zero, so there is only one "is the pop-up showing" flag
  // for either path to flip.
  useEffect(() => {
    if (expired) setVisible(false);
  }, [expired]);

  // Marked on DISPLAY, not on dismissal: the rule is one appearance per
  // session whether or not the viewer engages. Keyed off `data` because that
  // is what decides whether anything renders at all — a session where the
  // record is disabled, or the request failed, must not be burned.
  //
  // `alreadySeen` is the value captured at mount, so this write cannot hide
  // the pop-up it just marked.
  useEffect(() => {
    if (!data || alreadySeen) return;
    markPromoPopupSeen(seenKey);
  }, [data, alreadySeen, seenKey]);

  if (!data || !visible || alreadySeen) return null;

  // Resolved on every render, never latched: a user who gains entitlement
  // mid-session must not be stuck on a refusal button until a hard reload
  // (the bug TCX-914 shipped and then fixed).
  const button = resolveCta(data, user);
  // TCX-917 increment — resolved completely independently of the primary:
  // `undefined` whenever no secondary is configured at all.
  const secondaryButton = resolveSecondaryCta(data, user);

  // TCX-917 increment (preset redirect actions) — the ONE place either
  // button's click is carried out, shared by the primary and the secondary
  // so the two destinations (external link, support modal, internal route)
  // can never be handled two slightly different ways.
  //
  // `openExternal` REFUSES anything that is not an absolute http(s) URL (see
  // its own file), so an internal route (`kind: 'internal'`) must go through
  // the router instead — passing a relative path to `openExternal` would
  // always be refused. `destination` being `undefined` is only reachable for
  // an owner button whose own action is a preset that failed to resolve (see
  // `resolveButton`'s own comment) — there is nothing left to do, so this is
  // a deliberate no-op rather than a broken navigation.
  const runDestination = (destination: ResolvedCtaDestination | undefined) => {
    if (!destination) return;
    if (destination.kind === 'support') {
      setSupportOpen(true);
      return;
    }
    if (destination.kind === 'internal') {
      navigate(destination.path);
      return;
    }
    openExternal(destination.url);
  };

  const handleCtaClick = () => runDestination(resolveCtaDestination(button.action, user));

  const handleSecondaryCtaClick = () => {
    if (!secondaryButton) return;
    runDestination(resolveCtaDestination(secondaryButton.action, user));
  };

  // `undefined` (no progress line at all) whenever there is no timer — the
  // same "absent means unchanged" shape every optional field on this record
  // follows.
  const dismissProgress =
    durationMs !== null && remainingMs !== null ? remainingMs / durationMs : undefined;

  return (
    <div
      className="promo-popup-container"
      // TCX-917 increment (card width) — the record's own width, or the
      // contract default when it carries none. See `cardWidthVars` for why
      // this is a variable and not an inline `maxWidth`.
      style={cardWidthVars(data)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <PopupCard
        content={data}
        cta={button}
        secondaryCta={secondaryButton}
        dismissProgress={dismissProgress}
        onCtaClick={handleCtaClick}
        onSecondaryCtaClick={secondaryButton ? handleSecondaryCtaClick : undefined}
        onDismiss={() => setVisible(false)}
      />

      {supportOpen && (
        <Suspense fallback={null}>
          <RequestHelp open onCancel={() => setSupportOpen(false)} />
        </Suspense>
      )}
    </div>
  );
};

export default PromoPopup;
